import { addressComponentTypes, remainingComponents } from '../constants';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { getSelectedCountry, getSelectedRegion } from 'hooks/useAddressForm/helpers';
import React from 'react';

import { Address, LocationDetails } from '@alluradirect/react';
import * as Sentry from '@sentry/nextjs';

import { useFormikContext } from 'formik';

// Source: https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
// Useful for removing french accents (mon Dieu, c'est terrible!)
const removeAccents = (str: String) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export type InitialAddress = Pick<Address, 'STREET_NAME' | 'STREET_NUMBER' | 'CITY' | 'REGION' | 'COUNTRY'>;

function formatInitialAddress(initialAddress: InitialAddress): string {
  const { STREET_NAME, STREET_NUMBER, CITY, REGION, COUNTRY } = initialAddress;
  return `${STREET_NUMBER} ${STREET_NAME}, ${CITY}, ${REGION}, ${COUNTRY}`;
}

export const useAddressAutocomplete = (
  addressPrefix: string,
  locationDetails?: LocationDetails,
  initialAddress?: InitialAddress,
) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const [address, setAddress] = React.useState(initialAddress ? formatInitialAddress(initialAddress) : '');

  const clearAddressField = (field: string) => {
    switch (field) {
      case addressComponentTypes.city: {
        setFieldValue(`${addressPrefix}CITY`, ' ');
        setFieldValue(`${addressPrefix}CITY_ID`, 0);
        break;
      }

      case addressComponentTypes.region: {
        setFieldValue(`${addressPrefix}REGION`, ' ');
        setFieldValue(`${addressPrefix}REGION_ID`, 0);
        break;
      }

      case addressComponentTypes.postal_code: {
        setFieldValue(`${addressPrefix}POSTAL`, ' ');
        break;
      }

      case addressComponentTypes.country: {
        setFieldValue(`${addressPrefix}COUNTRY`, ' ');
        break;
      }

      case addressComponentTypes.street_number: {
        setFieldValue(`${addressPrefix}STREET_NUMBER`, ' ');
        break;
      }

      case addressComponentTypes.street_name: {
        setFieldValue(`${addressPrefix}STREET_NAME`, ' ');
        break;
      }
    }
  };

  const handleSelectAddress = (address: string) => {
    setAddress(address);
    setFieldValue(`${addressPrefix}UNIT`, '');
    geocodeByAddress(address)
      .then(results => {
        let notAutofilledFields = [...remainingComponents];

        getLatLng(results[0]);
        const addressComponents = results[0].address_components;

        addressComponents.forEach(addressComponent => {
          switch (addressComponent.types[0]) {
            case addressComponentTypes.city: {
              notAutofilledFields = notAutofilledFields.filter(field => field !== addressComponentTypes.city);
              setFieldValue(`${addressPrefix}CITY`, addressComponent.long_name);

              break;
            }
            case addressComponentTypes.region: {
              notAutofilledFields = notAutofilledFields.filter(field => field !== addressComponentTypes.region);
              setFieldValue(`${addressPrefix}REGION`, addressComponent.long_name);
              const region = removeAccents(addressComponent.long_name);

              setFieldValue(`${addressPrefix}REGION`, region);

              if (locationDetails) {
                const selectedRegion = getSelectedRegion(region, locationDetails.REGIONS);
                setFieldValue(`${addressPrefix}REGION_ID`, selectedRegion.ID);
              }

              break;
            }

            case addressComponentTypes.postal_code: {
              notAutofilledFields = notAutofilledFields.filter(field => field !== addressComponentTypes.postal_code);
              setFieldValue(`${addressPrefix}POSTAL`, addressComponent.long_name);
              break;
            }

            case addressComponentTypes.country: {
              notAutofilledFields = notAutofilledFields.filter(field => field !== addressComponentTypes.country);
              setFieldValue(`${addressPrefix}COUNTRY`, addressComponent.long_name);
              const country = removeAccents(addressComponent.long_name);

              setFieldValue(`${addressPrefix}COUNTRY`, country);
              if (locationDetails) {
                const selectedCountry = getSelectedCountry(country, locationDetails.COUNTRIES);
                setFieldValue(`${addressPrefix}COUNTRY_ID`, selectedCountry.ID);
              }
              break;
            }

            case addressComponentTypes.street_number:
              notAutofilledFields = notAutofilledFields.filter(field => field !== addressComponentTypes.street_number);
              setFieldValue(`${addressPrefix}STREET_NUMBER`, addressComponent.long_name);
              break;

            case addressComponentTypes.street_name:
              notAutofilledFields = notAutofilledFields.filter(field => field !== addressComponentTypes.street_name);
              setFieldValue(`${addressPrefix}STREET_NAME`, addressComponent.long_name);
              /**
               * https://github.com/jaredpalmer/formik/issues/2059
               * When the auto complete is used, the validation is firing before the values are set.
               * By using setTimeout we should be able to setFieldTouched after the values are set, which will
               * trigger the correct validation. This is a hacky solution, but it works. It only needs
               * to be used on one field, and it should be fine.
               */
              setTimeout(() => setFieldTouched(`${addressPrefix}STREET_NAME`, true));
              break;
          }
        });

        notAutofilledFields.forEach(field => {
          clearAddressField(field);
        });
      })
      .catch(error => Sentry.captureException(error));
  };

  return {
    address,
    setAddress,
    handleSelectAddress,
  };
};
